import type { IntlShape } from "react-intl";
import { dateFieldError, isBeforeBirthDate, isStartAfterEnd, todayIso } from "@/lib/date-instant";
import type { DateFieldError } from "@/lib/date-instant";

// Date rules for the profile's entry collections, in the EDITOR's key names.
//
// The in-line import review already enforces this exact rule set — format, not-future, start<=end and
// not-before-birth — via profile-merge-store's DATE_RANGE_SECTIONS / BIRTH_ANCHOR_FIELD. The normal
// editor called none of it, so the review refused values the editor saved: a certificate issued before
// the person was born, or Von 2024-06 / Bis 2020-01, both committed cleanly. genesis is no backstop —
// it validates that a start date is PRESENT on create and nothing else, never reads
// person_detail.date_of_birth, and neither date pair has a CHECK constraint.
//
// The review keys its rows `title` / `fieldOfStudy` / `issued`; the editor's descriptors use
// `jobTitle` / `field` / `date`. Hence a second table rather than importing the store's — same rules,
// different vocabulary. Keep the two in step; validation.alignment.test.ts asserts they agree.

/** The date that must not precede the person's birth date, per collection. */
export const BIRTH_ANCHOR: Partial<Record<string, string>> = {
  experience: "startDate",
  education: "startDate",
  certificate: "date",
};

/** Collections whose rows carry a start/end pair that must not be inverted. */
export const DATE_RANGE: Partial<Record<string, readonly [string, string]>> = {
  experience: ["startDate", "endDate"],
  education: ["startDate", "endDate"],
};

/** Every date-bearing field per collection, so each is shape/future checked. */
export const DATE_FIELDS: Partial<Record<string, readonly string[]>> = {
  experience: ["startDate", "endDate"],
  education: ["startDate", "endDate"],
  certificate: ["date"],
};


/**
 * Length caps for the entry collections' free-text fields, in the EDITOR's key names.
 *
 * Mirrors the genesis `ImportedProfileSanitizer` constants — EXPERIENCE_JOB_TITLE/EMPLOYER/DEPARTMENT/
 * LOCATION 128, EDUCATION_FIELD/INSTITUTION/CITY 256, CERTIFICATE_NAME 128, EXPERTISE_DESCRIPTION 64 —
 * which run on the IMPORT path only. On the normal mutations genesis caps none of them and every column
 * is unbounded `text`, so these are the only ceiling that exists. Unlike the person_detail caps, the
 * in-line import review does not enforce these either (`importFieldError` is never applied to collection
 * rows), so this is a gap being closed rather than a divergence being repaired.
 *
 * Kept in sync BY HAND, same as IMPORT_FIELD_MAX_CHARS.
 */
export const ENTRY_FIELD_MAX_CHARS: Readonly<Record<string, Readonly<Record<string, number>>>> = {
  experience: { jobTitle: 128, employer: 128, department: 128, location: 128 },
  education: { field: 256, institution: 256, city: 256 },
  certificate: { name: 128 },
  expertise: { description: 64 },
};

/**
 * The experience activities list. The editor holds it newline-joined (one task per line) and splits on
 * commit, so the cap is per LINE — mirroring EXPERIENCE_ACTIVITY_MAX_CHARS — and the count mirrors
 * EXPERIENCE_ACTIVITIES_MAX_ENTRIES, the only element-count cap genesis defines anywhere.
 */
export const ACTIVITY_MAX_CHARS = 1024;
export const ACTIVITIES_MAX_ENTRIES = 64;
const ACTIVITIES_FIELD = "activities";

/** A length problem in one row of a collection, or null. Reports the first offender. */
export function entryFieldError(
  kind: string,
  values: Record<string, unknown>,
): { field: string; max: number; kind: "too-long" | "too-many" } | null {
  for (const [field, max] of Object.entries(ENTRY_FIELD_MAX_CHARS[kind] ?? {})) {
    const value = values[field];
    if (value != null && String(value).length > max) {
      return { field, max, kind: "too-long" };
    }
  }

  if (kind === "experience") {
    const raw = values[ACTIVITIES_FIELD];
    // Blank lines are dropped on commit (linesToList), so they must not count towards either cap.
    const lines = String(raw ?? "")
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l !== "");
    if (lines.length > ACTIVITIES_MAX_ENTRIES) {
      return { field: ACTIVITIES_FIELD, max: ACTIVITIES_MAX_ENTRIES, kind: "too-many" };
    }
    if (lines.some((l) => l.length > ACTIVITY_MAX_CHARS)) {
      return { field: ACTIVITIES_FIELD, max: ACTIVITY_MAX_CHARS, kind: "too-long" };
    }
  }

  return null;
}

/** A date error for one row of a collection, or null. `birthDate` may be blank (then no birth check). */
export function entryDateError(
  kind: string,
  values: Record<string, unknown>,
  birthDate: string | null | undefined,
): { field: string; error: DateFieldError } | null {
  const read = (field: string) => {
    const v = values[field];
    return v == null ? "" : String(v);
  };

  // Shape and not-in-the-future, for every date the row carries.
  for (const field of DATE_FIELDS[kind] ?? []) {
    const error = dateFieldError(read(field), todayIso());
    if (error) return { field, error };
  }

  // An inverted range reports on the end field — that is the one the user just set.
  const range = DATE_RANGE[kind];
  if (range && isStartAfterEnd(read(range[0]), read(range[1]))) {
    return { field: range[1], error: "range" };
  }

  const anchor = BIRTH_ANCHOR[kind];
  if (anchor && isBeforeBirthDate(read(anchor), birthDate)) {
    return { field: anchor, error: "beforeBirth" };
  }

  return null;
}

/** The user-facing reason for a row's date error. Mirrors MergeDateWarning's copy, keyed the same way
 *  so the review and the editor say the same thing about the same value. */
export function entryDateMessage(intl: IntlShape, error: DateFieldError): string {
  switch (error) {
    case "future":
      return intl.formatMessage({ id: "profile.merge.date.future", defaultMessage: "This date can't be in the future." });
    case "range":
      return intl.formatMessage({ id: "profile.merge.date.range", defaultMessage: "End date must be on or after the start date." });
    case "beforeBirth":
      return intl.formatMessage({ id: "profile.merge.date.beforeBirth", defaultMessage: "This date is before the date of birth." });
    default:
      return intl.formatMessage({ id: "profile.merge.date.format", defaultMessage: "Use a date like YYYY, YYYY-MM or YYYY-MM-DD." });
  }
}

/** The user-facing reason for a row's length problem. Reuses the review's too-long copy. */
export function entryFieldMessage(
  intl: IntlShape,
  error: { max: number; kind: "too-long" | "too-many" },
): string {
  return error.kind === "too-many"
    ? intl.formatMessage(
        { id: "profile.page.error.tooManyActivities", defaultMessage: "Keep this to {max} entries or fewer." },
        { max: error.max },
      )
    : intl.formatMessage(
        { id: "profile.merge.field.tooLong", defaultMessage: "Keep this to {max} characters or fewer." },
        { max: error.max },
      );
}
